import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import {
  ProgramWeekCopyModal,
  ProgramWeekCopyModalRef,
} from "@modules/programs/components/programHeader/programWeekCopyModal"
import { ProgramInfinite, ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { useProgramSessionDuplicate } from "@modules/programSession/hooks/useProgramSessionDuplicate"
import { useSessionFormContext } from "@modules/sessions/hooks/useSessionFormContext"
import { useSessionSidebar } from "@modules/sessions/hooks/useSessionSidebar"
import { useProgramSessionEvents } from "@modules/training/hooks/useTraining"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import dayjs from "dayjs"
import { useMemo, useRef } from "react"
import { useTheme } from "styled-components"

type ProgramHeaderActionsViewProps = {
  program: ProgramInfinite | ProgramOnOff
}

export const ProgramHeaderActionsView: React.FC<ProgramHeaderActionsViewProps> = ({ program }) => {
  const { setProgram } = useSessionFormContext()
  const { navigateToSessionForm } = useSessionSidebar()
  const { refreshCalendarEvents, selectedDate } = useCalendar()
  const { sessions: programSessions } = useProgramSessionEvents(program.id)
  const { onCopyInfiniteProgramWeek, onCopyOnOffProgramWeek, weekCopied } = useProgramSessionDuplicate()

  const programWeekCopyModalRef = useRef<ProgramWeekCopyModalRef>(null)

  const canAddSession = program._programType !== ProgramOnOffTypeOf
  const currentWeekHasSessions = programSessions.length > 0
  const canCopyWeek = currentWeekHasSessions
  const canPasteWeek = useMemo(() => {
    if (!!weekCopied && program._programType === weekCopied.programType) {
      if (weekCopied.programType === ProgramInfiniteTypeOf && dayjs(selectedDate).weekday(6).isAfter()) {
        return (
          !dayjs(weekCopied.originStartDate).isSame(selectedDate, "week") || program.id !== weekCopied.originProgramId
        )
      } else if (weekCopied.programType === ProgramOnOffTypeOf) {
        return weekCopied.originStartIndex !== selectedDate || program.id !== weekCopied.originProgramId
      }
    }
    return false
  }, [weekCopied, program, selectedDate])

  const onAddSessionClick = async () => {
    if (program) {
      setProgram(program)
      await navigateToSessionForm(true, true, refreshCalendarEvents)
    }
  }

  const onCopyWeekClick = () => {
    if (program._programType === ProgramInfiniteTypeOf && !!selectedDate && typeof selectedDate !== "number") {
      onCopyInfiniteProgramWeek(
        program.id,
        ProgramInfiniteTypeOf,
        program.title,
        dayjs(selectedDate).weekday(0).toISOString(),
        dayjs(selectedDate).weekday(6).toISOString(),
      )
    } else if (program._programType === ProgramOnOffTypeOf && typeof selectedDate === "number") {
      onCopyOnOffProgramWeek(program.id, ProgramOnOffTypeOf, program.title, selectedDate, selectedDate + 6)
    }
  }

  const onPasteWeekClick = async () => {
    await programWeekCopyModalRef.current?.onPasteWeekClick()
  }

  return (
    <div className="card my-0 py-3 px-2 gap-2 flex flex-row align-items-center justify-content-evenly w-full">
      {/* Add session button */}
      <ActionButton
        action={onAddSessionClick}
        iconClassName={`p-button-icon pi pi-plus`}
        buttonLablel={word("program.createSession.button")}
        disabled={!canAddSession}
        colorType={"Primary"}
      />

      {/* Copy week button*/}
      <ActionButton
        action={onCopyWeekClick}
        iconClassName={`p-button-icon pi pi-copy`}
        buttonLablel={word("program.weekCopyPaste.copy.button")}
        disabled={!canCopyWeek}
        colorType={"Quaternary"}
      />

      {/* Paste week button*/}
      <ActionButton
        action={onPasteWeekClick}
        iconClassName={`p-button-icon pi pi-file-import`}
        buttonLablel={word("program.weekCopyPaste.paste.button")}
        disabled={!canPasteWeek}
        colorType={"Quaternary"}
      />

      <ProgramWeekCopyModal program={program} ref={programWeekCopyModalRef} />
    </div>
  )
}

type ActionButtonProps = {
  colorType: "Primary" | "Quaternary"
  action: () => void
  iconClassName: string
  buttonLablel: string
  disabled: boolean
}
const ActionButton: React.FC<ActionButtonProps> = ({ colorType, action, iconClassName, buttonLablel, disabled }) => {
  const theme = useTheme()

  return (
    <div className="flex flex-column align-items-center justify-content-between gap-1 text-center">
      <HUButton
        icon={{
          iconView: (
            <span
              className={iconClassName}
              style={{ color: disabled ? theme.neutralColor400 : theme.buttonPrimaryTextColor }}
            />
          ),
        }}
        type="Rounded"
        colorType={colorType}
        size="XS"
        onClick={action}
        disabled={disabled}
        className={disabled ? "bg-transparent border-transparent" : ""}
      />

      <HUText fontStyle="LS" style={{ color: disabled ? theme.neutralColor400 : theme.buttonPrimaryTextColor }}>
        {buttonLablel}
      </HUText>
    </div>
  )
}
